/**
 * English (United States) Messages
 * This file contains all messages for en_US locale
 */

// Shared ending of the "<item> added. ..." toast after saving a Twilio number, messaging service or alpha sender.
const CAN_NOW_SEND_MESSAGES = "You can now send messages to clients";

export const ERROR_MESSAGES = {
    // Template related errors
    TEMPLATE: {
        FRIENDLY_NAME_REQUIRED: "Enter friendlyName",
        FRIENDLY_NAME_REQUIRED_ALT: "Enter the friendly name",
        MESSAGE_REQUIRED: "Template content is required.",
        MODULE_REQUIRED: "Module selection is required",
    },

    // Form validation errors
    VALIDATION: {
        REQUIRED: "Required",
        PHONE_NUMBER_REQUIRED: "Phone number is required.",
        PHONE_NUMBER_INVALID: "Please provide a valid phone number.",
        NAME_REQUIRED: "Please provide a name.",
        EMAIL_REQUIRED: "Email is required",
        EMAIL_INVALID: "Please provide a valid email address",
        ALL_FIELDS_REQUIRED: "Please fill all fields and check your email format",
        SAVED_VIEW_NAME_REQUIRED: "Enter a name to save this view.",
        URL_INVALID: "Please enter a valid URL",
    },

    // Message related errors
    MESSAGE: {
        TO_NUMBER_REQUIRED: "To number is required.",
        MESSAGE_BODY_REQUIRED: "Message body is required.",
        FROM_NUMBER_REQUIRED: "From number is required.",
        SEND_FAILED: "Failed to send message",
        SEND_ERROR: "An error occurred while sending the message",
    },

    // General errors
    GENERAL: {
        SOMETHING_WENT_WRONG: "Something went wrong.",
        ERROR: "Error",
        UNKNOWN_ERROR: "An unexpected error occurred",
    },

    // Phone/Number related errors
    PHONE: {
        NUMBER_REQUIRED: "Please provide a phone number.",
        INVALID_FORMAT: "Please enter a valid phone number format",
    },

    // User/Admin related errors
    USER: {
        INVALID_CREDENTIALS: "Invalid credentials",
        ACCESS_DENIED: "Access denied",
        USER_NOT_FOUND: "User not found",
        ADD_USER_ERROR: "Please select one user to continue.",
        ALREADY_PART_OF_TEAM: "This user is already part of the team.",
        ASSIGN_NUMBER_REQUIRED: "Please assign a number to add the user.",
    },
    CHANNELS: {
        FRIENDLY_NAME_INVALID: "Enter a valid friendly name",
        NO_NUMBERS_CONNECTED: "No numbers connected.",
    },
    TEAMS: {
        SELECT_USER_REQUIRED: "Please select at least one user to create a team.",
        FRIENDLY_NAME_REQUIRED: "Please provide a friendly name to create a team.",
        MIN_ONE_USER_MUST_REMAIN: "At least one user must remain in the team.",
    },
    BLOCKED_NUMBERS: {
        // Substring to match API error when blocking a duplicate (English API message).
        ALREADY_BLOCKED_SUBSTRING: "already blocked",
    },
};

export const SUCCESS_MESSAGES = {
    // Template related success messages
    TEMPLATE: {
        ADDED: "Template added! Personalize client messages with ease.",
        UPDATED: "Template updated! Your message is refreshed.",
        AFTER_ADDING_MESSAGE: "Simplify your messaging with customizable templates for efficient communication.",
        DELETED: "Template deleted. Create a new one as needed.",
    },

    // Message related success messages
    MESSAGE: {
        SENT_SUCCESS: "Message sent successfully",
    },

    // General success messages
    GENERAL: {
        OPERATION_SUCCESS: "Operation completed successfully",
        SAVED: "Changes saved successfully",
        UPDATED: "Updated successfully",
        DELETED: "Deleted successfully",
    },

    // User/Admin related success messages
    USER: {
        CREATED: "User created successfully.",
        UPDATED: "User updated successfully.",
        DELETED: "User deleted. Feel free to add a new one when needed.",
    },
    CHANNELS: {
        TWILIO_CONFIGURED: "Service connected successfully! Proceed to add a phone number.",
        TWILIO_NUMBER_ADDED: `Phone number added. ${CAN_NOW_SEND_MESSAGES}`,
        CAN_NOW_SEND_MESSAGES,
    },
    TEAMS: {
        CREATED: "Team created successfully.",
        DELETED: "Team deleted successfully.",
    },
    BLOCKED_NUMBERS: {
        BLOCKED: "Messages can no longer be sent to or received from this contact or group.",
        UNBLOCKED: "Messages can now be sent to and received from this contact or group.",
    },
};

export const CONFIRMATION_MESSAGES = {
    // Template related confirmation messages
    TEMPLATE: {
        DELETE_CONFIRMATION: "Delete this template? It might still be used in automations or campaigns.",
    },

    // General confirmation messages
    GENERAL: {
        DELETE_CONFIRMATION: "Are you sure you want to delete?",
        SAVE_CONFIRMATION: "Do you want to save your changes?",
        CANCEL_CONFIRMATION: "Are you sure you want to cancel? Your changes will be lost.",
    },

    SCHEDULED_MESSAGES: {
        DELETE_ALL: "All scheduled messages will be canceled and permanently removed. This cannot be undone.",
    },
};

export const INFO_MESSAGES = {
    // Template related informational messages
    TEMPLATE: {
        INFO: "This template contains informational details.",
        NAME_EXAMPLE: "e.g. US Support, Sales",
    },

    INBOX: {
        STAY_CONNECTED: "Stay connected",
        STAY_CONNECTED_TO_INITIATE_NEW_MESSAGE:
            "Begin typing a contact name or number above to initiate a new message.",
        EMPTY_FILTER_VIEW: "Filtered View is Empty",
        EMPTY_FILTER_VIEW_TAGLINE: "Adjust or clear your filters to view conversations.",
        EMPTY_SAVED_VIEW: "Saved View is Empty.",
        EMPTY_SAVED_VIEW_TAGLINE: "Update your filters to view matching conversations.",
        EMPTY_UNREAD_FILTER_VIEW: "You're all caught up!",
        EMPTY_UNREAD_FILTER_VIEW_TAGLINE: "New unread messages will show up here.",
        NEW_MESSAGES_PLACEHOLDER: "New messages will show up here.",
        NO_OTHER_FROM_NUMBERS: "No other numbers have messaged this contact",
    },

    CAMPAIGN: {
        CSV_SUPPORTED_FORMAT: "Supported format: CSV",
        CSV_REQUIREMENT_HEADING: "CSV file requirement:",
    },

    LOGIN: {
        OTP_NOT_RECEIVED: "Didn't receive the OTP?",
    },

    VOICE: {
        CALLER_ID_VERIFICATION_FOLLOW_UP: "After entering the code, your caller ID will be verified and activated.",
    },

    // General informational messages
    GENERAL: {
        INFO: "For your information.",
    },

    CHANNELS: {
        CONFIGURE_NUMBERS_STEP: "In the right panel, you'll find all SMS-enabled numbers available for configuration.",
        TEST_CONNECTION: {
            title: "TEST YOUR CONNECTION",
            description: "Scan this QR code to open {serviceName} and send a test message.",
        },
    },
};

// Helper function to get error message by key path
export const getErrorMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = ERROR_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Error message not found for key path: ${keyPath}`);
            return ERROR_MESSAGES.GENERAL.UNKNOWN_ERROR;
        }
    }

    return message;
};

// Helper function to get success message by key path
export const getSuccessMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = SUCCESS_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Success message not found for key path: ${keyPath}`);
            return SUCCESS_MESSAGES.GENERAL.OPERATION_SUCCESS;
        }
    }

    return message;
};

// Helper function to get confirmation message by key path
export const getConfirmationMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = CONFIRMATION_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Confirmation message not found for key path: ${keyPath}`);
            return CONFIRMATION_MESSAGES.GENERAL.DELETE_CONFIRMATION;
        }
    }

    return message;
};

// Helper function to get info message by key path
export const getInfoMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = INFO_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Info message not found for key path: ${keyPath}`);
            return INFO_MESSAGES.GENERAL.INFO;
        }
    }

    return message;
};

// Export individual message categories for direct import
export const {
    TEMPLATE: TEMPLATE_ERRORS,
    VALIDATION,
    MESSAGE: MESSAGE_ERRORS,
    GENERAL: GENERAL_ERRORS,
    PHONE,
    USER: USER_ERRORS,
    TEAMS: TEAMS_ERRORS,
    BLOCKED_NUMBERS: BLOCKED_NUMBERS_ERRORS,
} = ERROR_MESSAGES;

export const {
    TEMPLATE: TEMPLATE_SUCCESS,
    MESSAGE: MESSAGE_SUCCESS,
    GENERAL: GENERAL_SUCCESS,
    USER: USER_SUCCESS,
    TEAMS: TEAMS_SUCCESS,
    BLOCKED_NUMBERS: BLOCKED_NUMBERS_SUCCESS,
} = SUCCESS_MESSAGES;

export const { TEMPLATE: TEMPLATE_CONFIRMATION, GENERAL: GENERAL_CONFIRMATION } = CONFIRMATION_MESSAGES;
export const { TEMPLATE: TEMPLATE_INFO, GENERAL: GENERAL_INFO } = INFO_MESSAGES;
